import {observable, action, computed} from 'mobx'

export class Signal {
  @observable type
  @observable indicators
  @observable fundamentals
  @observable alternatives
  @observable additional
  @observable part
  @observable value
  @observable validationErrors
  @observable wasInserted
  @observable insertFunc
  @observable insertState
  @observable loading

  constructor(rootStore) {
    this.rootStore = rootStore
    this.indicators = []
    this.fundamentals = []
    this.alternatives = []
    this.additional = false
    this.validationErrors = []
    this.wasInserted = false
    this.loading = false
  }

  @computed get text() {
    return this.rootStore.algo.single.data.signal || ''
  }

  @action change(name, value) {
    this[name] = value
  }

  @action insert(content = true) {
    let before = []
    let after = []
    let errors = []
    this.part.params.map(param => {
      if (
        (param.is_required && !param.value) ||
        (`${this.value}`.indexOf('X ') === 0 && !param.additionalValue)
      ) {
        errors.push(param.name)
        return errors
      }
      if (param.value) {
        let value = `${param.value}`
        if (param.ident !== 'TICKER') {
          value = value.replace('X', param.additionalValue)
        }
        if (value === 'Exact year') {
          value = param.additionalValue
        }
        if (param.syntax) {
          value = `${param.syntax}`.replace('{0}', value)
        }
        if (param.location === 'before') {
          before.push(value)
        }
        if (param.location === 'after') {
          after.push(value)
        }
      }
    })
    if (errors.length && content) {
      this.addValidationErrors(errors)
      return
    }
    if (typeof this.insertFunc === 'function') {
      before = before.join(' ')
      after = after.join(' ')
      this.insertState.text = content ? `${before} ${this.part.syntax} ${after}`.trim() : ''
      this.insertFunc(this.insertState)
    }
  }

  @action getParts = async () => {
    this.loading = true
    const resObj = await this.rootStore.api.request('sys/signal/supported-parts', 'get')
    this.indicators = resObj.indicators
    this.fundamentals = resObj.fundamentals
    this.alternatives = resObj.alternatives
    this.loading = false
    return resObj
  }

  @action getPart() {
    this.clearValidationErrors()
    this.wasInserted = false
    this.additional = false
    const type = this.type
    const partObj = this[type].find(row => row.syntax === this.value || row.ident === this.value)
    if (!partObj) {
      return
    }
    let {ident, kind, name, syntax, params} = partObj
    let defaultParams = []
    let additionalParams = []
    params.map(par => {
      const param = new Param(par, this)
      if (par.group === 'default') {
        defaultParams.push(param)
      }
      if (par.group === 'additional') {
        additionalParams.push(param)
      }
    })
    this.part = new Part({ident, kind, name, syntax, defaultParams, additionalParams})
  }

  @action addInput(name) {
    this.inputs = this.inputs.concat(name)
  }

  @action removeInput(name) {
    this.inputs = this.inputs.filter(input => input !== name)
  }

  @action setValues(values) {
    for (let name in values) {
      this[name] = values[name]
    }
  }

  @action addValidationErrors(e) {
    const errors = Array.isArray(e) ? e.concat() : [e]
    this.validationErrors = errors
  }

  @action removeValidationError(name) {
    this.validationErrors = this.validationErrors.filter(error => error !== name)
  }

  @action clearValidationErrors() {
    this.validationErrors = []
  }

  @action showAdditional() {
    this.additional = true
  }

  @action setWasInserted() {
    this.wasInserted = true
  }
}

class Part {
  @observable ident
  @observable kind
  @observable name
  @observable syntax
  @observable defaultParams
  @observable additionalParams

  constructor(props) {
    this.ident = props.ident
    this.kind = props.kind
    this.name = props.name
    this.syntax = props.syntax
    this.defaultParams = props.defaultParams
    this.additionalParams = props.additionalParams
  }

  @computed get params() {
    return this.defaultParams.concat(this.additionalParams)
  }
}

class Param {
  @observable value
  @observable additionalValue

  @observable ident
  @observable is_required
  @observable kind
  @observable location
  @observable name
  @observable options
  @observable syntax
  @observable filter

  constructor(props, signal) {
    this.signal = signal

    this.value = ''
    this.additionalValue = ''
    this.default_value = props.default_value

    this.ident = props.ident
    this.is_required = props.is_required
    this.kind = props.kind
    this.location = props.location
    this.name = props.name
    this.options = props.options ? props.options.indexOf('hist') !== -1 ? ['macd', 'signal', 'histogram'] : props.options : this.getOptions(props.ident)
    this.syntax = props.syntax
    this.filter = ''
  }

  @computed get hasNumberInput() {
    return this.options && (`${this.value}`.indexOf('X ') !== -1 || this.value === 'Exact year')
  }

  @computed get selectWithNumber() {
    return this.value.replace('X', '')
  }

  @action searchTickers(filter) {
    this.filter = filter
  }

  @action getOptions(ident) {
    let vals = []
    switch (ident) {
    case 'DAYS_BACK':
      vals = ['X days ago', 'X weeks ago', 'X months ago', 'last month', 'yesterday', 'today']
      break
    case 'NUM_DAYS':
      vals = ['X day', 'X week', 'X month']
      break
    case 'MONTH':
      vals = ['last month', 'X months ago', 'current month', 'trailing month', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
        'October', 'November', 'December']
      break
    case 'YEAR':
      vals = ['Exact year', 'X years ago', 'last year']
      break
    default:
      vals = null
    }
    return vals
  }

  @action changeValue(value, additional) {
    if (value.length && value < 1) {
      value = 1
    }
    if (additional) {
      this.additionalValue = value
    } else {
      if (this.value === 'Exact year') {
        this.additionalValue = 5
      } else if (value === 'Exact year') {
        this.additionalValue = new Date().getFullYear()
      }
      this.value = value
      if (this.hasNumberInput && !this.additionalValue) {
        this.additionalValue = 5
      }
    }
  }

  @action removeValidationError() {
    this.signal.removeValidationError(this.name)
  }
}
